import classNames from "classnames";
import { BillingSlider } from "components/BillingSlider/BillingSlider";
import { CheckoutModal } from "components/CheckoutModal/CheckoutModal";
import { ConditionalLink } from "components/conditionalLink";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import ConditionalToolTip from "components/design/conditionalToolTip";
import { TableUI } from "components/design/TableUI";
import ToolTip from "components/design/toolTip";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import Slider from "components/DesignSystem/Slider/Slider";
import Table from "components/DesignSystem/Table/V2/Table";
import { Tag } from "components/DesignSystem/Tag/Tag";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { ArrowRightUpV2 } from "components/icons/ArrowUpRightV2";
import { DISPLAY_DATE, YYYY_MM_DD } from "constants/date";
import { INKLE_CREDITS, SourceTitleKeys } from "constants/invoiceSource";
import dayjs from "dayjs";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { Preview } from "components/PreviewModal";
import { useWindowSize } from "hooks/useWindowSize";
import { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import AutopayIcon from "static/images/Autopay.svg";
import BankTransfer from "static/images/BankTransfer.svg";
import SmallBooks from "static/images/SmallBooks.svg";
import SmalMailroom from "static/images/SmallMailroom.svg";
import SmallSalesTax from "static/images/SmallSalesTax.svg";
import SmallTax from "static/images/SmallTax.svg";
import {
  useDeleteBillingInvoiceMutation,
  useGetBillingInvoicesQuery,
  useLazyGetBillingInvoicesQuery,
  useRevokeLateFeeMutation,
} from "store/apis/billingInvoice";
import { setPaymentTitle, setUsableCredit } from "store/slices/credit";
import { RootState } from "store/store";
import { AppsTypes, BillingInvoice } from "types/Models/billing";
import { BackendError } from "types/utils/error";
import { debounce } from "utils/debouncing";
import { formatDate } from "utils/formatDate";
import {
  FULL_REFUND,
  IN_PROGRESS,
  PAID,
  PARTIAL_REFUND,
} from "../../constants/billing";
import authContext from "../../jwt_context&axios/authContext";
import "../../static/styles/components/billing.css";
import { FileIcon } from "../design/FileIcon";
import Loader from "../DesignSystem/Loader/loader";
import { BillingInvoiceOptions } from "./BillingInvoiceOptions";
import { BillingInvoiceSlider } from "./BillingInvoiceSlider";
import { CreateOrEditInvoice } from "./CreateInvoice";
import { DraftInvoices } from "./DraftInvoices";
import { ExportBilling } from "./ExportBilling";
import { InvoiceFilter, STATUS_OPTIONS_MAP } from "./InvoiceFilter";
import { PayUnpaid } from "./payUnpaid";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import {
  billingInvoiceSortOptions,
  BILLING_SORT_BY,
} from "./billingInvoiceSortOptions";
import SortFunnel from "static/images/SortFunnel.svg";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import Wind from "components/icons/Wind";

const RefundTag = ({
  isPartialRefund,
  refundAmount,
  refundSource,
  refundDate,
}: {
  isPartialRefund: boolean;
  refundAmount: number;
  refundSource: SourceTitleKeys;
  refundDate: string;
}) => {
  const refundToBank = `A refund of $${refundAmount} was initiated on ${dayjs(
    refundDate
  ).format(DISPLAY_DATE)}.`;
  const refundToCredit = `Added ${refundAmount} Inkle credits (1 credit = 1$)`;
  const tooltipText =
    refundSource === INKLE_CREDITS ? refundToCredit : refundToBank;

  return (
    <ConditionalToolTip condition={tooltipText}>
      <span>
        <Tag tagType="gray">
          {isPartialRefund ? "Partial Refund" : "Full Refund"}
        </Tag>
      </span>
    </ConditionalToolTip>
  );
};

const ItemTags = ({ productType }: { productType: AppsTypes[] }) => {
  const uniqueProductType = [...new Set(productType)];

  const productMap = {
    TAX: {
      title: "Tax",
      icon: SmallTax,
    },
    BOOKS: {
      title: "Books",
      icon: SmallBooks,
    },
    MAILROOM: {
      title: "Mailroom",
      icon: SmalMailroom,
    },
    SALES_TAX: {
      title: "Sales Tax",
      icon: SmallSalesTax,
    },
    OTHER: {
      icon: null,
      title: "Others",
    },
    INTRA_GROUP: {
      icon: null,
      title: "Others",
    },
  };

  return (
    <div className="t-flex t-gap-1">
      {uniqueProductType.map((type, index) => {
        return (
          <>
            <div key={type} className="t-flex t-items-center t-gap-1">
              {productMap[type].icon && (
                <img
                  src={productMap[type].icon}
                  alt={productMap[type].title}
                  className="t-grayscale t-opacity-50"
                />
              )}
              <span className="t-text-body-sm t-text-neutral-40 t-font-medium">
                {productMap[type].title}
              </span>
            </div>
            {Boolean(
              index < uniqueProductType.length - 1 &&
                uniqueProductType.length > 1
            ) && (
              <span className="t-text-body-sm t-text-text-30">
                {Boolean(
                  uniqueProductType.length > 2 &&
                    index !== uniqueProductType.length - 2
                )
                  ? ","
                  : "and"}
              </span>
            )}
          </>
        );
      })}
    </div>
  );
};

export const RevokeLateFeeModal = ({
  isOpen,
  close,
  invoiceId,
}: {
  isOpen: boolean;
  close: () => void;
  invoiceId: string | null;
}) => {
  const [revokeLateFee, { isLoading }] = useRevokeLateFeeMutation();
  const { successToast } = useToast();
  const handleRevokeFee = async () => {
    try {
      if (invoiceId) {
        await revokeLateFee({
          invoice_id: invoiceId,
        }).unwrap();
        successToast({ message: "Late fee revoked successfully" });
      }

      close();
    } catch (error: any) {}
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Revoke late fee</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to remove the late fee for this invoice? This
          action will permanently update the invoice and stop any future late
          fees.
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button>Cancel</Button>
          <Button
            customType="danger"
            onClick={handleRevokeFee}
            isLoading={isLoading}
          >
            Revoke
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

export const Billing = ({ crmId }: { crmId?: string }) => {
  usePageTitle("Billing");
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const { alertToast, successToast } = useToast();
  const { groupEntities } = useContext(authContext);

  const [pageNumber, setPageNumber] = useState(1);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUnpaidPaymentModal, setShowUnpaidPaymentModal] = useState(false);
  const [invoicesToPay, setInvoicesToPay] = useState<BillingInvoice[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showInvoiceData, setShowInvoiceData] = useState(false);
  const [tableType, setTableType] = useState("ACTIVE");

  const dispatch = useDispatch();
  const { uuid } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const groupId = uuid || crmId;
  const { isAdmin, isForeignCA } = useRoleBasedView();
  const [
    deleteInvoice,
    { isLoading: deletingInvoice, originalArgs: deletingArgs },
  ] = useDeleteBillingInvoiceMutation();
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<string | null>();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);
  const { isMobile } = useWindowSize();
  const [allUnpaidInvoices, setAllUnpaidInvoices] = useState<BillingInvoice[]>(
    []
  );
  const [fetchUnpaidPage] = useLazyGetBillingInvoicesQuery();
  const showBillingSlider = useSelector(
    (store: RootState) => store.billingInvoiceSlider.showBillingSlider
  );

  const [sorting, setSorting] = useState<
    {
      desc: boolean;
      id: "created_at";
    }[]
  >([{ desc: true, id: "created_at" }]);

  const getSortingOrder = () => {
    if (selectedSortOption.sortKey === BILLING_SORT_BY.UNPAID_FIRST) {
      return BILLING_SORT_BY.UNPAID_FIRST;
    }
    if (sorting[0].id === "created_at") {
      if (sorting[0].desc) {
        return "RECENTLY_CREATED";
      } else {
        return "PREVIOUSLY_CREATED";
      }
    }
    return null;
  };

  const { values: filterValues, updateFilter } = useFilters({
    initialValue: {
      INVOICE_TYPE: [] as string[],
      SEASONS: [] as string[],
      STATUS: [] as string[],
      APPS: [] as string[],
      START_DATE: "",
      END_DATE: "",
      SELECT_PERIOD: "",
      SORT_BY: billingInvoiceSortOptions[0].value,
    },
    useQueryParams: true,
  });

  const clearFilters = () => {
    updateFilter("INVOICE_TYPE", []);
    updateFilter("SEASONS", []);
    updateFilter("STATUS", []);
    updateFilter("APPS", []);
    updateFilter("START_DATE", "");
    updateFilter("END_DATE", "");
    updateFilter("SELECT_PERIOD", "");
    updateFilter("SORT_BY", billingInvoiceSortOptions[0].value);
  };

  const areAllKeysEmpty = (obj: Record<string, unknown>) => {
    const { SORT_BY: _, ...rest } = obj as Record<string, unknown> & {
      SORT_BY?: unknown;
    };

    if (Object.keys(rest).length === 0) return true;

    return Object.values(rest).every((value) => {
      if (!value) return true;
      if (Array.isArray(value)) return value.length === 0;

      return false;
    });
  };

  const selectedSortOption =
    billingInvoiceSortOptions.find((o) => o.value === filterValues.SORT_BY) ??
    billingInvoiceSortOptions[0];

  let payload = {
    groupId: groupId!,
    pageNumber,
    searchTerm,
    selectedSeasons: filterValues.SEASONS.join(","),
    billing_types: filterValues.INVOICE_TYPE.join(","),
    paymentStatuses: filterValues.STATUS.map((status) =>
      status === "UNPAID" ? STATUS_OPTIONS_MAP.UNPAID.join(",") : status
    ).join(","),
    app_types: filterValues.APPS.join(","),
    entityId,
    sortBy: getSortingOrder(),
    fromDate: filterValues.START_DATE
      ? dayjs(filterValues.START_DATE).format(YYYY_MM_DD)
      : null,
    toDate: filterValues.END_DATE
      ? dayjs(filterValues.END_DATE).format(YYYY_MM_DD)
      : null,
  };

  if (tableType === "DRAFT") {
    // @ts-ignore
    payload = {
      groupId: groupId!,
      pageNumber,
      searchTerm,
      entityId,
      paymentStatuses: "DRAFT",

      selectedSeasons: filterValues.SEASONS.join(","),
      billing_types: filterValues.INVOICE_TYPE.join(","),
      app_types: filterValues.APPS.join(","),
      sortBy: getSortingOrder(),
      fromDate: filterValues.START_DATE
        ? dayjs(filterValues.START_DATE).format(YYYY_MM_DD)
        : null,
      toDate: filterValues.END_DATE
        ? dayjs(filterValues.END_DATE).format(YYYY_MM_DD)
        : null,
    };
  }

  const {
    data: {
      group_invoices: invoices = [],
      total_pages = 0,
      current_page = 0,
      per_page = 0,
      total_count = 0,
      due_amount: dueAmount = 0,
    } = {},
    isLoading,
    isFetching,
    isSuccess,
  } = useGetBillingInvoicesQuery(payload, {
    skip: !groupId || !entityId,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (current_page) {
      setPageNumber(current_page);
    }
  }, [current_page]);

  useEffect(() => {
    const fetchAllUnpaidInvoices = async () => {
      if (!groupId || !entityId) return;

      const baseParams = {
        groupId: groupId!,
        entityId,
        paymentStatuses: STATUS_OPTIONS_MAP.UNPAID.join(","),
      };

      const firstPage = await fetchUnpaidPage({
        ...baseParams,
        pageNumber: 1,
      }).unwrap();

      const remainingPages = await Promise.all(
        Array.from({ length: firstPage.total_pages - 1 }, (_, i) =>
          fetchUnpaidPage({ ...baseParams, pageNumber: i + 2 }).unwrap()
        )
      );

      const accumulated = [
        ...firstPage.group_invoices,
        ...remainingPages.flatMap((page) => page.group_invoices),
      ];

      setAllUnpaidInvoices(accumulated);
    };

    fetchAllUnpaidInvoices();
  }, [groupId, entityId, total_count, fetchUnpaidPage]);

  const unPaidInvoice = allUnpaidInvoices;

  const openCheckoutModal = (invoice: BillingInvoice) => {
    dispatch(setUsableCredit(true));
    dispatch(setPaymentTitle("Billings payment"));
    setInvoicesToPay([invoice]);
    setShowPaymentModal(true);
  };

  const isInvoicePaid = (status: string) => {
    return [PAID, PARTIAL_REFUND, FULL_REFUND].includes(status);
  };

  const closeCheckoutModal = () => {
    setShowPaymentModal(false);
  };

  const closeDocumentPreview = () => setPreviewFileId(null);

  const openPreviewAndDownloadLink = (uuid: string) => {
    setPreviewFileId(uuid);
  };

  const confirmDelete = async () => {
    if (groupId && deleteInvoiceId && entityId) {
      try {
        setDeleteInvoiceId(null);
        await deleteInvoice({
          groupId,
          entityId,
          invoiceId: deleteInvoiceId,
        }).unwrap();
        successToast({ message: "Invoice deleted" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };
  const columnHelper = createColumnHelper<BillingInvoice>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Invoice Name",
        size: 20,
        cell: (info) => {
          const rowData = info.row.original;
          const refundDetails =
            Array.isArray(rowData.refund_data) && rowData.refund_data[0];
          const {
            late_fee,
            status,
            paid_on,
            due_date,
            is_auto_pay_enabled,
            is_in_bank_transfer,
          } = rowData || {};

          return (
            <div className="t-flex t-items-center t-gap-2">
              <div>{info.getValue()}</div>
              {due_date && is_auto_pay_enabled && (
                <ToolTip text={`Due on ${formatDate(due_date)}`}>
                  <img src={AutopayIcon} alt="AutopayIcon" />
                </ToolTip>
              )}
              {refundDetails && (
                <RefundTag
                  isPartialRefund={refundDetails.type === "PARTIAL"}
                  refundAmount={refundDetails.amount}
                  refundSource={refundDetails.destination}
                  refundDate={refundDetails.refund_created_date}
                />
              )}
              {!isInvoicePaid(status) && !paid_on && Number(late_fee) > 0 && (
                <Tag tagType="red" icon={false} rounded={true}>
                  Overdue
                </Tag>
              )}
              {is_in_bank_transfer && (
                <ToolTip text="The bank transfer is in progress for this invoice.">
                  <img src={BankTransfer} alt="BankTransfer" />
                </ToolTip>
              )}
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("uuid", {
        header: "ITEM",
        size: 34,
        cell: (info) => {
          const rowData = info.row.original;
          const tasksToShow = rowData.tasks?.slice(1);

          return (
            <div>
              {rowData.product_types &&
              !Boolean(
                rowData.product_types.includes("OTHER") ||
                  rowData.product_types.includes("INTRA_GROUP")
              ) ? (
                <ItemTags productType={rowData.product_types} />
              ) : (
                <span className="t-text-neutral-40 t-text-body-sm t-block">
                  Others
                </span>
              )}

              <p className="t-m-0 t-flex t-items-center t-gap-x-1">
                {rowData?.task?.uuid ? (
                  <ConditionalLink
                    to={`/filings/${rowData?.task?.uuid}`}
                    target="__blank"
                    onClick={(e) => e.stopPropagation()}
                    className="t-group t-text-text-60 t-flex t-items-center hover:t-text-purple"
                  >
                    {rowData?.short_name}
                    <span className="t-text-purple t-ml-2.5 t-hidden group-hover:t-inline-flex">
                      <ArrowRightUpV2 />
                    </span>
                  </ConditionalLink>
                ) : (
                  <span className="t-text-text-60 t-cursor-default">
                    {Boolean(rowData.tasks?.length)
                      ? rowData.tasks?.[0].title
                      : rowData?.short_name}
                  </span>
                )}
                {Boolean(rowData.tasks && rowData.tasks.length > 1) && (
                  <ToolTip
                    text={
                      <>
                        {tasksToShow?.map((task) => {
                          return (
                            <span
                              key={task.uuid}
                              className="t-text-neutral-0 t-text-body-sm t-block t-m-0"
                            >
                              {task.title}
                            </span>
                          );
                        })}
                      </>
                    }
                    side="right"
                  >
                    <span className="t-bg-neutral-0 t-text-text-30 t-text-caption t-p-1 t-rounded-sm t-leading-none">
                      +{rowData.tasks!.length - 1}
                    </span>
                  </ToolTip>
                )}
              </p>
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("created_at", {
        header: "Date of Creation",
        size: 14,
        cell: (info) => formatDate(info.getValue()),
        enableSorting: false,
      }),
      columnHelper.accessor("document.uuid", {
        header: () => <div className="t-w-full t-text-center">Invoice</div>,
        size: 8,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <div className="t-flex t-justify-center">
              <span className="invoiceViewDownload">
                {rowData?.document ? (
                  <span
                    className="invoiceIcon"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreviewAndDownloadLink(rowData?.document?.uuid!);
                    }}
                  >
                    <FileIcon
                      fileType={rowData?.document?.file_type}
                      size="30"
                    />
                  </span>
                ) : (
                  <>-</>
                )}
              </span>
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("receipt_filename", {
        header: () => <div className="t-w-full t-text-center">Receipt</div>,
        size: 8,
        cell: (info) => {
          const rowData = info.row.original;
          return (
            <div className="t-flex t-justify-center">
              <span className="invoiceViewDownload">
                {Boolean(rowData?.receipt_document) ? (
                  <span
                    className="invoiceIcon"
                    onClick={(e) => {
                      e.stopPropagation();
                      openPreviewAndDownloadLink(
                        rowData?.receipt_document?.uuid!
                      );
                    }}
                  >
                    <FileIcon
                      fileType={rowData?.receipt_document?.file_type}
                      size="30"
                    />
                  </span>
                ) : (
                  <>-</>
                )}
              </span>
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("paid_on", {
        header: () => <div className="t-w-full t-text-right">STATUS</div>,
        size: 12,
        cell: (info) => {
          const invoice = info.row.original;
          const {
            status,
            amount,
            paid_on,
            is_auto_pay_enabled,
            invoice_type,
            paid_amount,
          } = invoice;

          let tooltipText =
            isInvoicePaid(status) &&
            paid_on &&
            `Paid $${paid_amount} on ${formatDate(paid_on)}`;

          if (Array.isArray(invoice?.refund_data)) {
            tooltipText = `Paid $${paid_amount} on ${formatDate(
              paid_on
            )} with a refund of $${invoice.refund_data[0].amount}.`;
          }

          const isDisabled = checkDisable({ amount, status });

          const handlePayClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            openCheckoutModal(invoice);
          };

          if (
            invoice_type === "MONTHLY_BOOKKEEPING" &&
            is_auto_pay_enabled === false
          ) {
            return (
              <div className="t-flex t-justify-end">
                <ToolTip text="Please contact our support team">
                  <span>
                    <Button
                      customType="primary"
                      disabled
                      size="small"
                      block
                      type="button"
                    >
                      <span className="t-flex t-gap-1">
                        <span>On hold</span>
                        <AmountSuperScript amount={Number(amount)} />
                      </span>
                    </Button>
                  </span>
                </ToolTip>
              </div>
            );
          }

          return (
            <div className="t-flex t-justify-end">
              <ConditionalToolTip
                condition={
                  Boolean(isInvoicePaid(status) && paid_on) && tooltipText
                }
              >
                <span>
                  <Button
                    customType={isDisabled ? "ghost" : "primary"}
                    className={classNames({
                      "t-font-medium !t-text-green-80 t-pointer-events-none":
                        isDisabled,
                    })}
                    size="small"
                    block
                    onClick={handlePayClick}
                  >
                    {isDisabled ? (
                      <span className="t-flex t-gap-1">
                        <span>Paid</span>{" "}
                        <AmountSuperScript amount={Number(paid_amount)} />
                      </span>
                    ) : (
                      <span className="t-flex t-gap-1">
                        <span>Pay</span>{" "}
                        <AmountSuperScript amount={Number(amount)} />
                      </span>
                    )}
                  </Button>
                </span>
              </ConditionalToolTip>
            </div>
          );
        },
        enableSorting: false,
      }),
      columnHelper.display({
        header: " ",
        size: 4,
        cell: (info) => (
          <BillingInvoiceOptions
            invoice={info.row.original}
            isInvoicePaid={isInvoicePaid}
            setDeleteInvoiceId={setDeleteInvoiceId}
          />
        ),
        enableSorting: false,
        id: "billing_invoice_options",
      }),
    ],
    [groupEntities, deletingInvoice, deletingArgs?.invoiceId, isMobile]
  );

  const checkDisable = ({
    amount,
    status,
  }: {
    amount: string;
    status: string;
  }) => {
    return (
      isInvoicePaid(status) ||
      status === IN_PROGRESS ||
      isNaN(Number(amount.replace("$", "")))
    );
  };

  const data =
    useMemo(
      () =>
        invoices?.map((bill) => {
          if (isNaN(Number(bill.amount))) return bill;
          return { ...bill, amount: bill?.amount };
        }),
      [invoices]
    ) || [];

  const paySelectedBill = () => {
    dispatch(setUsableCredit(true));
    setShowPaymentModal(true);
  };

  const openUnpaidBillModal = () => {
    setShowUnpaidPaymentModal(true);
    setShowInvoiceData(false);
  };

  const closeUnpaidBillModal = () => {
    setShowUnpaidPaymentModal(false);
  };

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
    manualSorting: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    // @ts-ignore
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const { link } = useConstructInternalLink();

  const { isOpen, open, close } = useModal();

  const debouncedSearch = debounce((e) => {
    setSearchTerm(e.target.value);
  });

  const onChangeTableType = (value: "ACTIVE" | "DRAFT") => {
    if (value === "DRAFT") {
      setPageNumber(1);
      setSearchTerm("");
      updateFilter("INVOICE_TYPE", []);
      updateFilter("SEASONS", []);
      updateFilter("STATUS", ["DRAFT"]);
      updateFilter("APPS", []);
      updateFilter("START_DATE", "");
      updateFilter("END_DATE", "");
      updateFilter("SELECT_PERIOD", "");
    }

    if (value === "ACTIVE") {
      updateFilter("STATUS", ["UNPAID"]);
    }

    setTableType(value);
  };

  const sortOptionsByKey = Object.fromEntries(
    billingInvoiceSortOptions.map((option) => [option.sortKey, option])
  );

  const sortGroups = [
    {
      label: "Default",
      options: [sortOptionsByKey[BILLING_SORT_BY.UNPAID_FIRST]],
    },
    {
      label: "Sort by creation date",
      options: [
        sortOptionsByKey[BILLING_SORT_BY.CREATED_LATEST_FIRST],
        sortOptionsByKey[BILLING_SORT_BY.CREATED_OLDEST_FIRST],
      ],
    },
  ];

  const handleSortChange = (value: string) => {
    const option = billingInvoiceSortOptions.find((o) => o.value === value);
    if (!option) return;

    updateFilter("SORT_BY", value);

    if (option.sortKey === BILLING_SORT_BY.CREATED_LATEST_FIRST) {
      setSorting([{ desc: true, id: "created_at" }]);
    } else if (option.sortKey === BILLING_SORT_BY.CREATED_OLDEST_FIRST) {
      setSorting([{ desc: false, id: "created_at" }]);
    }
  };

  return (
    <DashboardContainer className="t-h-full">
      <div className="t-flex t-flex-col t-gap-4 t-h-full">
        <PayUnpaid
          PayUnpaid={openUnpaidBillModal}
          paySelectedBill={paySelectedBill}
          unPaidInvoice={unPaidInvoice}
          invoicesToPay={invoicesToPay}
          showInvoiceData={showInvoiceData}
          setShowInvoiceData={setShowInvoiceData}
          dueAmount={dueAmount}
          invoicesCount={total_count}
        />
        <div className="t-flex t-gap-4 t-items-start sm:t-flex-col sm:t-items-stretch sm:t-gap-3">
          <div className="t-flex t-items-center t-gap-2 t-flex-wrap t-flex-1">
            {isAdmin && (
              <ToggleGroup
                size="xsmall"
                type="single"
                value={tableType}
                onValueChange={onChangeTableType}
              >
                <ToggleGroupItem value="ACTIVE">Active</ToggleGroupItem>
                <ToggleGroupItem value="DRAFT">Draft</ToggleGroupItem>
              </ToggleGroup>
            )}
            <Search
              className="t-w-[18%] focus:t-w-1/4 t-transition-all sm:t-w-full sm:focus:t-w-full"
              onChange={debouncedSearch}
              placeholder="Search"
            />
            <Dropdown.Root>
              <Dropdown.Trigger asChild>
                <Button size="xsmall" onClick={(e) => e.stopPropagation()}>
                  <div className="t-flex t-items-center t-gap-1.5">
                    <img
                      src={SortFunnel}
                      alt="icon"
                      height="16px"
                      width="16px"
                    />
                    <span className="t-text-body-sm t-text-text-90 t-font-medium">
                      Sort by: {selectedSortOption?.title}
                    </span>
                  </div>
                </Button>
              </Dropdown.Trigger>
              <Dropdown.Portal>
                <Dropdown.Content
                  sideOffset={4}
                  align="start"
                  side="bottom"
                  className="t-p-2"
                  onCloseAutoFocus={(e) => e.preventDefault()}
                >
                  <Radio.Root
                    value={selectedSortOption?.value}
                    onValueChange={handleSortChange}
                    className="t-space-y-[5px]"
                  >
                    {sortGroups.map((group) => (
                      <div key={group.label}>
                        <span className="t-text-text-30 t-text-body-sm t-w-full t-flex t-items-center t-self-stretch t-px-3">
                          {group.label}
                        </span>
                        <Radio.Content className="t-flex t-flex-col t-gap-y-1.5 t-py-1.5 t-px-2">
                          {group.options.map((option) => (
                            <Radio.Item
                              key={option.value}
                              value={option.value}
                              className="t-text-text-100 t-text-body-sm t-flex t-items-center t-gap-x-2"
                            >
                              {option.title}
                            </Radio.Item>
                          ))}
                        </Radio.Content>
                      </div>
                    ))}
                  </Radio.Root>
                </Dropdown.Content>
              </Dropdown.Portal>
            </Dropdown.Root>
            <InvoiceFilter values={filterValues} updateFilter={updateFilter} />
          </div>
          <div className="t-flex t-items-center t-gap-3 t-shrink-0 sm:t-justify-end">
            {isAdmin && tableType === "ACTIVE" && groupId && (
              <ExportBilling
                groupId={groupId}
                filterValues={filterValues}
                searchTerm={searchTerm}
              />
            )}
            {isAdmin && tableType === "ACTIVE" && (
              <Button customType="primary" onClick={open} size="small">
                Create
              </Button>
            )}
            <PaginationHandler
              page_num={pageNumber}
              per_page={per_page}
              total_count={total_count}
              total_pages={total_pages}
              onPageNumChange={setPageNumber}
            />
          </div>
        </div>
        <div className="t-flex-1 t-min-h-0 t-overflow-y-auto">
          <Async.Root
            isLoading={isLoading || isFetching}
            isEmpty={invoices?.length === 0}
            isSuccess={Boolean(isSuccess)}
          >
            <Async.Empty>
              <div className="t-flex t-flex-col t-items-center t-justify-center t-h-80 t-bg-[#F9F9FA] t-text-neutral-10 t-gap-y-5 t-rounded-lg">
                <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-2">
                  <Wind className="t-size-6 t-text-neutral-10" />
                  <div className="t-text-center">
                    <span className="t-m-0 t-text-body-sm t-text-text-60">
                      No{" "}
                      {areAllKeysEmpty(filterValues) ? "invoices" : "results"}{" "}
                      found
                    </span>
                    <p className="t-text-text-30 t-text-body-sm">
                      {areAllKeysEmpty(filterValues)
                        ? "You do not have any invoice for this entity"
                        : "Your current filters don’t match any invoices"}
                    </p>
                  </div>
                  {!areAllKeysEmpty(filterValues) && (
                    <Button size="small" type="button" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  )}
                </div>
              </div>
            </Async.Empty>
            <Async.Success>
              {tableType === "ACTIVE" ? (
                <TableUI table={table} enableSort>
                  {({ row }) => (
                    <Table.Row
                      onRowClick={() => {
                        history.push(
                          link(`/${row.original.uuid}`, {
                            keys: ["entity"],
                            includeUrl: true,
                          })
                        );
                      }}
                      key={row.original.uuid}
                      className="hover:t-bg-surface-lighter-grey"
                    >
                      {row.getVisibleCells().map((cell) => {
                        return (
                          <Table.Cell
                            key={cell.id}
                            style={{
                              width: `${cell.column.getSize()}%`,
                              padding: "12px 8px",
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  )}
                </TableUI>
              ) : (
                <DraftInvoices invoices={invoices} />
              )}
              <div className="t-px-3 t-py-2.5 t-invisible">
                invisible div so that the last row isn't obscured by floating
                view cart bar
              </div>
              {showBillingSlider && <BillingInvoiceSlider />}
            </Async.Success>
          </Async.Root>
          {showPaymentModal && (
            <CheckoutModal
              entityId={entityId}
              type="invoices"
              open={showPaymentModal}
              onClose={closeCheckoutModal}
              invoices={invoicesToPay}
              onInvoicePaid={closeCheckoutModal}
              payerUserType={isForeignCA && "FCA"}
            />
          )}
          {showUnpaidPaymentModal && (
            <CheckoutModal
              type="invoices"
              entityId={entityId}
              open={showUnpaidPaymentModal}
              onClose={closeUnpaidBillModal}
              invoices={unPaidInvoice}
              onInvoicePaid={closeUnpaidBillModal}
              payerUserType={isForeignCA && "FCA"}
            />
          )}
        </div>
        <Switch>
          <Route exact path={`${path}/:invoiceId`}>
            <Slider.Root
              open
              onOpenChange={() => history.push(`${url}${search}`)}
            >
              <Slider.Content open>
                <BillingSlider openCheckoutModal={openCheckoutModal} />
              </Slider.Content>
            </Slider.Root>
          </Route>
        </Switch>

        <Modal.Root
          open={Boolean(deleteInvoiceId)}
          onOpenChange={() => setDeleteInvoiceId(null)}
        >
          <Modal.Content>
            <Modal.Header>
              <Modal.Title>Delete Invoice?</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body>
              <div className="">
                Are you sure you want to delete this invoice? This action can
                not be undone.
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="t-flex t-justify-end t-gap-3">
                <Button onClick={() => setDeleteInvoiceId(null)}>Cancel</Button>
                <Button onClick={confirmDelete} customType="danger">
                  Confirm
                </Button>
              </div>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>

        {isOpen && <CreateOrEditInvoice isOpen={isOpen} close={close} />}

        <Preview
          showModal={Boolean(previewFileId)}
          closeModal={closeDocumentPreview}
          groupId={groupId}
          previewId={previewFileId}
        />
      </div>
    </DashboardContainer>
  );
};
